<script setup lang="ts">
import {
  $getProjectFileGroupPages,
  $getProjectFileReassignOptions,
  $reassignProjectFile,
  $getS3FileDownloadUrls,
  getFileManagementFolders,
  type FileManagementFolder,
  type ProjectFileGroupPageRequest,
  type ProjectFileGroupPagesRequest,
  type ProjectFileGroupPagesResponse,
  type ProjectFileGroupRecord,
  type ProjectFilesBatchScope,
  type FileReassignOption,
} from '@/api/fileManagement'
import { getFileIcon, getFileIconColor } from '@/helpers/utils/files/getFileIcon'
import { isPreviewableFile } from '@/helpers/utils/files/filePreview'
import { downloadFreshFiles } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { useSnackbarStore } from '@/store/snackbar'
import { formatDateTimeCanada24 } from '@/helpers/utils/dateFormatterHelper'
import { can } from '@layouts/plugins/casl'
import { useResizableDialog } from '@/helpers/composables/useResizableDialog'
import DivTable, { type DivTableOptions, type DivTableSortItem } from '@/components/vuetify-custom-components/DivTable.vue'
import TableColumnSettingsDialog from '@/components/projects/table-column-settings/TableColumnSettingsDialog.vue'
import { useUtilityStore } from '@/store/utility'
import type { TableHeader } from '@/models/general/table'
import ability from '@/plugins/casl/ability'
import { onWaitingRoomAutoSyncRefresh } from '@/helpers/events/waitingRoomAutoSync'

interface Props {
  modelValue: boolean
  projectId: number | string | string[] | null | undefined
  projectTypeId?: number | null
  projectScopes?: ProjectFileScope[]
}

const props = withDefaults(defineProps<Props>(), {
  projectTypeId: null,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()
const utilityStore = useUtilityStore()

const {
  dialogWidth,
  dialogHeight,
  dialogMinWidth,
  dialogMinHeight,
} = useResizableDialog({
  defaultWidth: 1200,
  defaultHeight: 820,
  minWidth: 760,
  minHeight: 560,
})

type ProjectFileItem = {
  id: string
  key: string
  selectionKey: string
  projectId: number | string
  projectName?: string | null
  folder: string
  filename: string
  previewUrl?: string | null
  identity: string
  list_state?: 'main' | 'historical'
  linked_items: {
    sign_ids: string[]
    line_item_ids: string[]
    kop_item_ids: string[]
  }
  statuses: string[]
  size?: number
  modified?: string
  created?: string
  versionCount?: number
  isHistorical?: boolean
}

type ProjectFileScope = {
  projectId: number | string
  projectName?: string | null
  selectedItems?: ProjectFileSelectedItem[]
}

type ProjectFileSelectedItem = {
  id: number
  identity?: string | null
}

type WaitingRoomProjectOption = {
  id: number
  name: string
  type?: number | null
  event_group?: number | null
  event_group_name?: string | null
}

type ProjectFolderCard = {
  title: string
  folder: string
  files: ProjectFileItem[]
  count: number
  page: number
  perPage: number
  totalPages: number
}

type ProjectFileGroup = {
  key: string
  title: string
  projectId: number | string
  projectName?: string | null
  selectedItems: ProjectFileSelectedItem[]
  count: number
  folderCounts: Record<string, number>
}

type LoadedProjectFilePage = ProjectFolderCard & {
  requestKey: string
  fetchedAt: number
}

// ── Per-file action loading state ────────────────────────────────────────────
const loadingDownload = ref<Set<string>>(new Set())
const loadingPreview = ref<Set<string>>(new Set())
const bulkDownloadScope = ref<string | null>(null)
const bulkDownloadLoading = computed(() => bulkDownloadScope.value !== null)
const selectedFileKeys = ref<string[]>([])
const selectedFileRows = ref<Record<string, ProjectFileItem>>({})

// ── Preview overlay ──────────────────────────────────────────────────────────
const previewUrl = ref<string | null>(null)
const previewFilename = ref<string>('')
const showPreviewDialog = ref(false)
const isPreviewLoading = ref(false)
let previewRequestId = 0

const closePreview = () => {
  previewRequestId += 1
  showPreviewDialog.value = false
  isPreviewLoading.value = false
  previewUrl.value = null
  previewFilename.value = ''
}

const previewDialogVisible = computed({
  get: () => showPreviewDialog.value,
  set: value => {
    showPreviewDialog.value = value
    if (!value) {
      closePreview()
    }
  },
})

const handleDownload = async (file: ProjectFileItem) => {
  if (!file.projectId) return

  loadingDownload.value = new Set([...loadingDownload.value, file.key])
  try {
    const data = await $getS3FileDownloadUrls(file.projectId, {
      key: file.key,
      intent: 'download',
    })
    const url = data?.url ?? data?.download_url
    if (!url) throw new Error('No download URL returned')

    const link = document.createElement('a')
    link.href = url
    link.download = file.filename
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to download file.',
    })
  } finally {
    const next = new Set(loadingDownload.value)
    next.delete(file.key)
    loadingDownload.value = next
  }
}

const selectedFiles = computed(() => {
  return selectedFileKeys.value
    .map(key => selectedFileRows.value[key])
    .filter((file): file is ProjectFileItem => !!file)
})

const selectedFilesByProject = computed(() => {
  const filesByProject = new Map<string, ProjectFileItem[]>()
  for (const file of selectedFiles.value) {
    const projectKey = String(file.projectId)
    const projectFiles = filesByProject.get(projectKey) || []
    projectFiles.push(file)
    filesByProject.set(projectKey, projectFiles)
  }

  return filesByProject
})

const getSelectedFilesForGroup = (group: ProjectFileGroup) => (
  selectedFilesByProject.value.get(group.key) || []
)

const projectDownloadScopeKey = (group: ProjectFileGroup) => `project:${group.key}`

const handleBulkDownload = async (
  filesToDownload: ProjectFileItem[],
  scope: string,
) => {
  if (!filesToDownload.length || bulkDownloadLoading.value) return

  bulkDownloadScope.value = scope
  try {
    const summary = await downloadFreshFiles(filesToDownload.map(getFreshDownloadTarget))
    const failedCount = summary.failedCount

    snackbarStore.showMessage({
      color: failedCount ? 'warning' : 'success',
      icon: failedCount ? 'mdi-alert-circle' : 'mdi-checkbox-marked-circle',
      title: failedCount ? 'Download Partially Complete' : 'Download Complete',
      text: failedCount
        ? `${summary.successCount} of ${filesToDownload.length} selected files downloaded. ${failedCount} failed.`
        : `${summary.successCount} selected ${summary.successCount === 1 ? 'file' : 'files'} downloaded.`,
    })
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to download the selected files.',
    })
  } finally {
    bulkDownloadScope.value = null
  }
}

const handleProjectBulkDownload = (group: ProjectFileGroup) => (
  handleBulkDownload(getSelectedFilesForGroup(group), projectDownloadScopeKey(group))
)

const handlePreview = async (file: ProjectFileItem) => {
  if (!file.projectId) return

  const requestId = previewRequestId + 1
  previewRequestId = requestId
  previewFilename.value = file.filename
  previewUrl.value = null
  showPreviewDialog.value = true
  isPreviewLoading.value = true
  loadingPreview.value = new Set([...loadingPreview.value, file.key])
  try {
    const data = await $getS3FileDownloadUrls(file.projectId, {
      key: file.key,
      intent: 'preview',
    })
    const url = data?.url ?? data?.download_url
    if (!url) throw new Error('No preview URL returned')

    if (requestId !== previewRequestId || !showPreviewDialog.value) return

    previewUrl.value = url
  } catch (error: any) {
    if (requestId !== previewRequestId) return
    showPreviewDialog.value = false
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Preview Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to preview file.',
    })
  } finally {
    const next = new Set(loadingPreview.value)
    next.delete(file.key)
    loadingPreview.value = next
    if (requestId === previewRequestId) {
      isPreviewLoading.value = false
    }
  }
}

const fileSearch = ref<string | null>('')
const selectedPrograms = ref<Array<{ id: number; name: string }>>([])
const programOptions = ref<Array<{ id: number; name: string }>>([])
const metadataLoading = ref(false)
const loadingPageGroups = ref<Set<string>>(new Set())
const filesLoading = computed(() => metadataLoading.value || loadingPageGroups.value.size > 0)
const loadedScopes = ref<ProjectFileScope[]>([])
const loadedGroupCounts = ref<Record<string, { count: number; folderCounts: Record<string, number> }>>({})
const loadedPages = ref<Record<string, LoadedProjectFilePage>>({})
const hasLoadedFiles = ref<boolean>(false)
const projectAccordionModel = ref<string[]>([])
const selectedFolderByGroup = ref<Record<string, string>>({})
const folderPaginationState = ref<Record<string, { page: number; perPage: number }>>({})
const showControlDialog = ref(false)
const selectedControlFile = ref<ProjectFileItem | null>(null)
const showRenameDialog = ref(false)
const showRenameConfirmDialog = ref(false)
const renameFile = ref<ProjectFileItem | null>(null)
const renameOptions = ref<FileReassignOption[]>([])
const selectedRenameOption = ref<FileReassignOption | null>(null)
const renameLoading = ref(false)
const renameOptionsLoading = ref(false)
const showWaitingRoomDialog = ref(false)
const showTableColumnSettingsDialog = ref(false)
const brokenPreviewUrls = ref<Set<string>>(new Set())
let fileSearchDebounce: ReturnType<typeof setTimeout> | null = null
let requestGeneration = 0
let metadataController: AbortController | null = null
const pageRequests = new Map<string, {
  requestKey: string
  controller: AbortController
  promise: Promise<void>
}>()
const allFoldersValue = '__ALL__'
const defaultFolderPerPage = 10
const folderPerPageOptions = [
  { title: '5', value: 5 },
  { title: '10', value: 10 },
  { title: '25', value: 25 },
  { title: '50', value: 50 },
]

const availableFolders = computed(() => getFileManagementFolders(props.projectTypeId))
const fileTableGridStorageId = 'project-files-dialog-file-table'
const fileTableColumnSettingsStorageKey = `${fileTableGridStorageId}-column-settings`

const defaultFileTableHeaders: TableHeader[] = [
  { key: 'preview', title: 'Preview', selectable: false, sortable: false, width: 60 },
  { key: 'actions', title: 'Actions', selectable: false, sortable: false, width: 150 },
  { key: 'filename', title: 'Filename', sortable: true },
  { key: 'statuses', title: 'Statuses', sortable: true },
  { key: 'created', title: 'Upload Date', sortable: true },
  { key: 'linked_items', title: 'Linked Items', sortable: true },
  { key: 'versions', title: 'Versions', sortable: true },
  { key: 'folder', title: 'Folder', sortable: true },
]

const cloneHeaders = (headers: TableHeader[]) => headers.map(header => ({ ...header }))

const mergeFileTableHeaders = (current: TableHeader[]) => {
  const defaultsByKey = new Map(defaultFileTableHeaders.map(header => [header.key, header]))
  const merged: TableHeader[] = []
  const seen = new Set<string>()

  current.forEach(header => {
    const defaultHeader = defaultsByKey.get(header.key)
    if (!defaultHeader || seen.has(header.key)) {
      return
    }

    seen.add(header.key)
    merged.push({
      ...defaultHeader,
      ...header,
      title: defaultHeader.title || header.title,
      selectable: defaultHeader.selectable === false ? false : header.selectable,
      sortable: defaultHeader.sortable === false ? false : header.sortable ?? defaultHeader.sortable ?? true,
      align: header.align ?? defaultHeader.align ?? '',
    })
  })

  defaultFileTableHeaders.forEach(header => {
    if (seen.has(header.key)) {
      return
    }

    seen.add(header.key)
    merged.push({ ...header })
  })

  return merged
}

const fileTableHeaders = ref<TableHeader[]>(
  mergeFileTableHeaders(utilityStore.getTableColumnSettings(fileTableColumnSettingsStorageKey)),
)
const fileTableSortBy = ref<DivTableSortItem[]>([])

const resolvedProjectId = computed<number | string | null>(() => {
  if (Array.isArray(props.projectId)) {
    return props.projectId[0] ?? null
  }

  return props.projectId ?? null
})

const requestScopes = computed<ProjectFileScope[]>(() => {
  if (props.projectScopes?.length) {
    const scopesByProjectId = new Map<string, ProjectFileScope>()
    props.projectScopes
      .filter(scope => scope.projectId !== null && scope.projectId !== undefined && scope.projectId !== '')
      .forEach(scope => {
        const key = String(scope.projectId)
        const existingScope = scopesByProjectId.get(key)
        const selectedItemsById = new Map<number, ProjectFileSelectedItem>(
          existingScope?.selectedItems?.map(item => [item.id, item]) || [],
        )

        for (const item of scope.selectedItems || []) {
          if (!selectedItemsById.has(item.id)) {
            selectedItemsById.set(item.id, item)
          }
        }

        scopesByProjectId.set(key, {
          projectId: existingScope?.projectId ?? scope.projectId,
          projectName: existingScope?.projectName ?? scope.projectName ?? null,
          selectedItems: Array.from(selectedItemsById.values()),
        })
      })

    return Array.from(scopesByProjectId.values())
  }

  if (!resolvedProjectId.value) {
    return []
  }

  return [{
    projectId: resolvedProjectId.value,
    projectName: null,
    selectedItems: [],
  }]
})

const requestScopesKey = computed(() => JSON.stringify(requestScopes.value))

const showProgramFilter = computed(() => {
  const projectIds = new Set(requestScopes.value.map(scope => String(scope.projectId)))
  return projectIds.size !== 1
})

const selectedProgramIds = computed(() => selectedPrograms.value.map(program => program.id))

const waitingRoomProjectOptions = computed<WaitingRoomProjectOption[]>(() => {
  const scopeMap = new Map<number, WaitingRoomProjectOption>()
  const scopes = loadedScopes.value.length ? loadedScopes.value : requestScopes.value

  for (const scope of scopes) {
    const id = Number(scope.projectId)
    if (!Number.isFinite(id)) {
      continue
    }

    scopeMap.set(id, {
      id,
      name: scope.projectName || `Project ${scope.projectId}`,
      type: props.projectTypeId,
    })
  }

  return Array.from(scopeMap.values())
})

const scopeKey = (scope: Pick<ProjectFileScope, 'projectId'>) => String(scope.projectId)

const getFreshDownloadTarget = (file: ProjectFileItem) => ({
  projectId: file.projectId,
  fileId: file.id,
  filename: file.filename,
})

const getGroupTitle = (scope: ProjectFileScope) => scope.projectName || `Project ${scope.projectId}`

const getSelectedItemLabel = (item: ProjectFileSelectedItem) => (
  item.identity?.trim() || 'No identity'
)

const getSelectedIdentities = (group: ProjectFileGroup) => Array.from(
  new Set(group.selectedItems.map(getSelectedItemLabel)),
)

const getFolderPagination = (folder: string) => {
  return folderPaginationState.value[folder] || {
    page: 1,
    perPage: defaultFolderPerPage,
  }
}

const setFolderPage = (folder: string, page: number) => {
  const current = getFolderPagination(folder)
  folderPaginationState.value = {
    ...folderPaginationState.value,
    [folder]: {
      ...current,
      page,
    },
  }
}

const setFolderPerPage = (folder: string, perPage: number) => {
  const current = getFolderPagination(folder)
  folderPaginationState.value = {
    ...folderPaginationState.value,
    [folder]: {
      ...current,
      page: 1,
      perPage,
    },
  }
}

const resetFolderPages = () => {
  folderPaginationState.value = {}
}

const isAllFoldersSelected = (folder: string) => folder === allFoldersValue

const projectGroups = computed<ProjectFileGroup[]>(() => {
  const scopesSource = hasLoadedFiles.value ? loadedScopes.value : requestScopes.value
  return scopesSource.map(scope => {
    const counts = loadedGroupCounts.value[scopeKey(scope)]
    return {
      key: scopeKey(scope),
      title: getGroupTitle(scope),
      projectId: scope.projectId,
      projectName: scope.projectName,
      selectedItems: scope.selectedItems || [],
      folderCounts: counts?.folderCounts || {},
      count: availableFolders.value.reduce((total, folder) => (
        total + (counts?.folderCounts[folder.value] || 0)
      ), 0),
    }
  })
})

const visibleProjectGroups = computed(() => {
  const groups = projectGroups.value
  if (!fileSearch.value?.trim() || groups.length <= 1) {
    return groups
  }

  return groups.filter(group => group.count > 0)
})

const canCollapseProjectAccordions = computed(() => visibleProjectGroups.value.length > 1)

const getDefaultFolderValue = () => availableFolders.value[0]?.value || ''

const getSelectedFolderForGroup = (group: ProjectFileGroup) => {
  const selected = selectedFolderByGroup.value[group.key]
  if (selected === allFoldersValue) {
    return selected
  }

  if (selected && availableFolders.value.some(folder => folder.value === selected)) {
    return selected
  }

  return getDefaultFolderValue()
}

const getSelectedFolderCard = (group: ProjectFileGroup): ProjectFolderCard => {
  const folder = getSelectedFolderForGroup(group)
  const pagination = getFolderPagination(folder)
  const loaded = loadedPages.value[group.key]
  const count = isAllFoldersSelected(folder) ? group.count : group.folderCounts[folder] || 0
  const matchingPage = loaded?.folder === folder
    && loaded.page === pagination.page && loaded.perPage === pagination.perPage
  return {
    title: isAllFoldersSelected(folder) ? 'All' : availableFolders.value.find(option => option.value === folder)?.title || folder,
    folder,
    files: matchingPage ? loaded.files : [],
    count: matchingPage ? loaded.count : count,
    page: pagination.page,
    perPage: pagination.perPage,
    totalPages: Math.max(1, Math.ceil((matchingPage ? loaded.count : count) / pagination.perPage)),
  }
}

const setSelectedFolderForGroup = (groupKey: string, folder: string) => {
  selectedFolderByGroup.value = {
    ...selectedFolderByGroup.value,
    [groupKey]: folder,
  }
  if (projectAccordionModel.value.includes(groupKey)) {
    void loadProjectPage(groupKey)
  }
}

const getFileTableGridId = (groupKey: string) => {
  const normalizedGroupKey = groupKey.replace(/[^a-zA-Z0-9_-]/g, '-')

  return `project-files-dialog-file-table-${normalizedGroupKey}`
}

const onFileTableOptionsUpdate = async (folder: string, options: DivTableOptions) => {
  const nextSortBy = options.sortBy || []
  if (JSON.stringify(nextSortBy) !== JSON.stringify(fileTableSortBy.value)) {
    fileTableSortBy.value = nextSortBy
    resetFolderPages()
    loadedPages.value = {}
    await loadOpenProjectPages()
    return
  }

  const currentPagination = getFolderPagination(folder)

  if (options.itemsPerPage !== currentPagination.perPage) {
    await onFolderPerPageUpdate(folder, options.itemsPerPage)
    return
  }

  if (options.page !== currentPagination.page) {
    await onFolderPageUpdate(folder, options.page)
  }
}

const onUpdateTableColumns = (headers: TableHeader[]) => {
  const mergedHeaders = mergeFileTableHeaders(headers)
  fileTableHeaders.value = cloneHeaders(mergedHeaders)
  utilityStore.setTableColumnSettings(fileTableColumnSettingsStorageKey, mergedHeaders)
}

const closeDialog = () => {
  emit('update:modelValue', false)
}

const openControlDialog = (file: ProjectFileItem) => {
  if (!ability.can('view', 'fmfilecontrol')) {
    return
  }

  selectedControlFile.value = file
  showControlDialog.value = true
}

const canReassign = (file: ProjectFileItem) => ability.can('reassign', 'fmfilecontrol')
  && file.list_state === 'main' && !file.isHistorical

const loadRenameOptions = async (search = '') => {
  if (!renameFile.value?.projectId) return
  renameOptionsLoading.value = true
  try {
    const response = await $getProjectFileReassignOptions(renameFile.value.projectId, {
      file_id: renameFile.value.id,
      search,
      per_page: 100,
    })
    renameOptions.value = response.data.results
  } catch (error: any) {
    snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Rename Failed', text: error?.response?.data?.detail || 'Unable to load available names.' })
  } finally {
    renameOptionsLoading.value = false
  }
}

const openRenameDialog = (file: ProjectFileItem) => {
  if (!canReassign(file)) return
  renameFile.value = file
  selectedRenameOption.value = null
  renameOptions.value = []
  showRenameDialog.value = true
  void loadRenameOptions()
}

const confirmRename = () => {
  if (selectedRenameOption.value) showRenameConfirmDialog.value = true
}

const submitRename = async () => {
  if (!renameFile.value || !selectedRenameOption.value) return
  renameLoading.value = true
  try {
    const response = await $reassignProjectFile(renameFile.value.projectId, {
      file_id: renameFile.value.id,
      target_item_id: selectedRenameOption.value.id,
    })
    snackbarStore.showMessage({ color: 'success', icon: 'mdi-checkbox-marked-circle', title: 'Success', text: response.data.detail || 'File successfully renamed.' })
    showRenameConfirmDialog.value = false
    showRenameDialog.value = false
    renameFile.value = null
    await getProjectFiles()
  } catch (error: any) {
    snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Rename Failed', text: error?.response?.data?.detail || error?.message || 'Unable to rename file.' })
  } finally {
    renameLoading.value = false
  }
}

const onDialogModelValueUpdate = (nextModelValue: boolean) => {
  emit('update:modelValue', nextModelValue)
}

const formatFileSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) {
    return '-'
  }

  if (bytes < 1024) {
    return `${bytes} B`
  }

  const kb = bytes / 1024
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`
  }

  return `${(kb / 1024).toFixed(1)} MB`
}

const formatDate = (value?: string) => formatDateTimeCanada24(value)

const getLinkedItemsCount = (file: ProjectFileItem) => {
  return file.linked_items.sign_ids.length
    + file.linked_items.line_item_ids.length
    + file.linked_items.kop_item_ids.length
}

const getFileSubtitle = (file: ProjectFileItem) => {
  const versionLabel = file.versionCount && file.versionCount > 1
    ? ` | Versions: ${file.versionCount}`
    : ''

  return `${formatDate(file.created)} | Linked items: ${getLinkedItemsCount(file)}${versionLabel}`
}

const shouldDisplayFileRecord = (file: ProjectFileGroupRecord) => {
  const responseFile = file as unknown as { is_archived?: boolean; list_state?: string }

  return responseFile.is_archived !== true && responseFile.list_state !== 'archived'
}

const mapApiFileToDialogFile = (file: ProjectFileGroupRecord, scope?: ProjectFileScope): ProjectFileItem => {
  const extension = (file.extension || '').trim()
  const filename = file.filename || (extension ? `${file.name}${extension}` : file.name)
  const projectId = file.campaign_id || scope?.projectId || resolvedProjectId.value || ''

  return {
    id: file.id,
    key: file.key || file.path,
    selectionKey: `${projectId}:${file.key || file.path || file.id}`,
    projectId,
    projectName: file.campaign_name ?? scope?.projectName ?? null,
    folder: file.folder,
    filename,
    identity: file.identity || file.name,
    list_state: file.list_state || 'main',
    linked_items: {
      sign_ids: file.linked_items?.sign_ids || [],
      line_item_ids: file.linked_items?.line_item_ids || [],
      kop_item_ids: file.linked_items?.kop_item_ids || [],
    },
    statuses: file.statuses || [],
    previewUrl: file.preview_url || null,
    isHistorical: file.is_historical ?? false,
    modified: file.updated_at || file.modified,
    created: file.created_at || file.created,
    versionCount: file.version_count,
  }
}

const isNonMainRow = (file: ProjectFileItem) => {
  return file.list_state === 'historical'
}

const getFileTitle = (file: ProjectFileItem) => {
  return file.filename
}

const getActionColor = (file: ProjectFileItem) => {
  if (isNonMainRow(file)) {
    return 'grey'
  }
  return getFileIconColor(file.filename)
}

const hasFileThumbnail = (file: ProjectFileItem) => {
  return !!file.previewUrl && !brokenPreviewUrls.value.has(file.previewUrl)
}

const markPreviewBroken = (url?: string | null) => {
  if (!url) return
  brokenPreviewUrls.value = new Set([...brokenPreviewUrls.value, url])
}

const getFileListRequest = (): ProjectFileGroupPagesRequest => ({
  scopes: requestScopes.value.map(scope => ({
    project_id: scope.projectId,
    ...(scope.selectedItems?.length
      ? { selected_item_ids: scope.selectedItems.map(item => item.id) }
      : {}),
  } satisfies ProjectFilesBatchScope)),
  search: fileSearch.value?.trim() || undefined,
  program_ids: selectedProgramIds.value,
  campaign_type: requestScopes.value.length ? undefined : props.projectTypeId,
  ordering: fileTableSortBy.value[0]
    ? `${fileTableSortBy.value[0].order === 'desc' ? '-' : ''}${fileTableSortBy.value[0].key}`
    : 'filename',
})

const cancelFileRequests = () => {
  requestGeneration += 1
  metadataController?.abort()
  metadataController = null
  for (const request of pageRequests.values()) request.controller.abort()
  pageRequests.clear()
  loadingPageGroups.value = new Set()
  metadataLoading.value = false
}

const showFileLoadError = (error: any) => {
  snackbarStore.showMessage({
    color: 'error',
    icon: 'mdi-alert-circle',
    title: 'Failed to Load Files',
    text: error?.response?.data?.detail || error?.message || 'Unable to fetch project files.',
  })
}

const getProjectPageRequest = (groupKey: string): ProjectFileGroupPageRequest => {
  const folder = selectedFolderByGroup.value[groupKey] || getDefaultFolderValue()
  const pagination = getFolderPagination(folder)
  return {
    project_id: groupKey,
    folder: isAllFoldersSelected(folder) ? null : folder as FileManagementFolder,
    page: pagination.page,
    per_page: pagination.perPage,
  }
}

const getPageRequestKey = (page: ProjectFileGroupPageRequest) => JSON.stringify([
  requestGeneration, page.project_id.toString(), page.folder, page.page, page.per_page,
  getFileListRequest().ordering,
])

const applyProjectPage = (
  page: ProjectFileGroupPagesResponse['pages'][number],
  requestKey: string,
) => {
  const groupKey = String(page.project_id)
  if (getPageRequestKey(getProjectPageRequest(groupKey)) !== requestKey) return
  const folder = page.folder || allFoldersValue
  const scope = loadedScopes.value.find(scope => scopeKey(scope) === groupKey)
  const rows = page.results.filter(shouldDisplayFileRecord).map(file => mapApiFileToDialogFile(file, scope))
  loadedPages.value = {
    ...loadedPages.value,
    [groupKey]: {
      title: folder === allFoldersValue ? 'All' : availableFolders.value.find(option => option.value === folder)?.title || folder,
      folder,
      files: rows,
      count: page.count,
      page: page.page,
      perPage: page.per_page,
      totalPages: page.total_pages,
      requestKey,
      fetchedAt: Date.now(),
    },
  }
  const selectedKeys = new Set(selectedFileKeys.value)
  const selectedRows = { ...selectedFileRows.value }
  for (const file of rows) {
    if (selectedKeys.has(file.selectionKey)) selectedRows[file.selectionKey] = file
  }
  selectedFileRows.value = selectedRows
}

const loadProjectPage = async (groupKey: string): Promise<void> => {
  if (!props.modelValue || !hasLoadedFiles.value || metadataController || fileSearchDebounce) return
  if (!loadedScopes.value.some(scope => scopeKey(scope) === groupKey)) return
  const page = getProjectPageRequest(groupKey)
  const requestKey = getPageRequestKey(page)
  const loaded = loadedPages.value[groupKey]
  // Thumbnail URLs last 15 minutes. Reopening a panel refreshes an older page.
  if (loaded?.requestKey === requestKey && Date.now() - loaded.fetchedAt < 840000) return
  const pending = pageRequests.get(groupKey)
  if (pending?.requestKey === requestKey) return pending.promise
  pending?.controller.abort()
  const controller = new AbortController()
  const generation = requestGeneration
  loadingPageGroups.value = new Set([...loadingPageGroups.value, groupKey])
  const promise = (async () => {
    try {
      const response = await $getProjectFileGroupPages({
        ...getFileListRequest(),
        include_metadata: false,
        pages: [page],
      }, controller.signal)
      if (controller.signal.aborted || generation !== requestGeneration || !props.modelValue) return
      if (pageRequests.get(groupKey)?.controller !== controller) return
      for (const result of response.data.pages) applyProjectPage(result, requestKey)
    } catch (error: any) {
      if (!controller.signal.aborted && generation === requestGeneration) showFileLoadError(error)
    } finally {
      if (pageRequests.get(groupKey)?.controller === controller) {
        pageRequests.delete(groupKey)
        const loading = new Set(loadingPageGroups.value)
        loading.delete(groupKey)
        loadingPageGroups.value = loading
      }
    }
  })()
  pageRequests.set(groupKey, { requestKey, controller, promise })
  return promise
}

const loadOpenProjectPages = async (folder?: string) => {
  const groups = projectGroups.value.filter(group => (
    projectAccordionModel.value.includes(group.key)
    && (!folder || getSelectedFolderForGroup(group) === folder)
  ))
  const generation = requestGeneration
  let nextGroup = 0
  // A refresh can cover many expanded Projects. Keep reads bounded rather than
  // starting a request for every panel at once.
  await Promise.all(Array.from({ length: Math.min(4, groups.length) }, async () => {
    while (nextGroup < groups.length && generation === requestGeneration && props.modelValue) {
      const group = groups[nextGroup++]
      if (group && projectAccordionModel.value.includes(group.key)) await loadProjectPage(group.key)
    }
  }))
}

const getProjectFiles = async () => {
  if (fileSearchDebounce) {
    clearTimeout(fileSearchDebounce)
    fileSearchDebounce = null
  }
  cancelFileRequests()
  if (!props.modelValue) return
  if (!requestScopes.value.length && props.projectTypeId === null) {
    loadedScopes.value = []
    loadedPages.value = {}
    loadedGroupCounts.value = {}
    programOptions.value = []
    hasLoadedFiles.value = false
    return
  }

  const generation = requestGeneration
  const controller = new AbortController()
  metadataController = controller
  metadataLoading.value = true
  const singleScope = requestScopes.value.length === 1 ? requestScopes.value[0] : undefined
  const initialGroups = projectAccordionModel.value.length
    ? [...projectAccordionModel.value]
    : singleScope ? [scopeKey(singleScope)] : []
  const pages = initialGroups.map(getProjectPageRequest)
  const pageKeys = new Map(pages.map(page => [String(page.project_id), getPageRequestKey(page)]))
  const selectionSnapshot = new Set(selectedFileKeys.value)
  try {
    const response = await $getProjectFileGroupPages({
      ...getFileListRequest(),
      include_metadata: true,
      pages,
      selected_files: selectedFiles.value.map(file => ({ project_id: file.projectId, key: file.key })),
    }, controller.signal)
    if (controller.signal.aborted || generation !== requestGeneration || !props.modelValue) return

    const nextCounts: typeof loadedGroupCounts.value = {}
    loadedScopes.value = response.data.scopes.map(scopeResponse => {
      const originalScope = requestScopes.value.find(scope => String(scope.projectId) === String(scopeResponse.project_id))
      nextCounts[String(scopeResponse.project_id)] = {
        count: scopeResponse.count,
        folderCounts: scopeResponse.folder_counts as Record<string, number>,
      }
      return {
        projectId: scopeResponse.project_id,
        projectName: scopeResponse.campaign_name ?? originalScope?.projectName ?? null,
        selectedItems: originalScope?.selectedItems || [],
      }
    })
    loadedGroupCounts.value = nextCounts
    loadedPages.value = {}
    programOptions.value = response.data.program_options || []
    const selectedRows = { ...selectedFileRows.value }
    for (const file of response.data.selected_files || []) {
      const key = `${file.project_id}:${file.key}`
      const selected = selectedRows[key]
      if (selected) selectedRows[key] = { ...selected, id: file.file_id, filename: file.filename }
    }
    selectedFileRows.value = selectedRows
    const validKeys = new Set(response.data.valid_selection_keys || [])
    selectedFileKeys.value = selectedFileKeys.value.filter(key => !selectionSnapshot.has(key) || validKeys.has(key))
    for (const page of response.data.pages) {
      const requestKey = pageKeys.get(String(page.project_id))
      if (requestKey) applyProjectPage(page, requestKey)
    }
    hasLoadedFiles.value = true
    metadataController = null
    await nextTick()
    if (generation === requestGeneration) await loadOpenProjectPages()
  } catch (error: any) {
    if (!controller.signal.aborted && generation === requestGeneration) showFileLoadError(error)
  } finally {
    if (generation === requestGeneration) {
      if (metadataController === controller) metadataController = null
      metadataLoading.value = false
    }
  }
}

watch(selectedFileKeys, keys => {
  const availableRows = { ...selectedFileRows.value }
  for (const page of Object.values(loadedPages.value)) {
    for (const file of page.files) availableRows[file.selectionKey] = file
  }
  selectedFileRows.value = Object.fromEntries(keys
    .filter(key => !!availableRows[key])
    .map(key => [key, availableRows[key]]))
}, { flush: 'sync' })

const unsubscribeWaitingRoomAutoSyncRefresh = onWaitingRoomAutoSyncRefresh(result => {
  if (!props.modelValue) return

  const projectIds = new Set((requestScopes.value.length ? requestScopes.value : loadedScopes.value)
    .map(scope => String(scope.projectId)))
  if (result.affected_targets.some(target => target.project_id && projectIds.has(String(target.project_id)))) {
    void getProjectFiles()
  }
})

watch(
  fileSearch,
  () => {
    if (!props.modelValue) {
      return
    }

    cancelFileRequests()
    if (fileSearchDebounce) {
      clearTimeout(fileSearchDebounce)
    }

    fileSearchDebounce = setTimeout(() => {
      fileSearchDebounce = null
      resetFolderPages()
      getProjectFiles()
    }, 300)
  },
)

watch(
  selectedProgramIds,
  () => {
    if (!props.modelValue) {
      return
    }

    resetFolderPages()
    getProjectFiles()
  },
)

watch(
  () => props.modelValue,
  value => {
    if (value) {
      getProjectFiles()
      return
    }

    cancelFileRequests()
    if (fileSearchDebounce) {
      clearTimeout(fileSearchDebounce)
      fileSearchDebounce = null
    }

    fileSearch.value = ''
    selectedPrograms.value = []
    selectedFileKeys.value = []
    selectedFileRows.value = {}
    programOptions.value = []
    selectedFolderByGroup.value = {}
    fileTableSortBy.value = []
    resetFolderPages()
    projectAccordionModel.value = []
    loadedScopes.value = []
    loadedPages.value = {}
    loadedGroupCounts.value = {}
    hasLoadedFiles.value = false
    selectedControlFile.value = null
  },
)

watch(
  [resolvedProjectId, requestScopesKey, () => props.projectTypeId],
  () => {
    if (props.modelValue) {
      resetFolderPages()
      getProjectFiles()
    }
  },
)

watch(
  visibleProjectGroups,
  groups => {
    if (!groups.length) {
      projectAccordionModel.value = []
    }
    else if (groups.length === 1) {
      projectAccordionModel.value = [groups[0].key]
    }
    else {
      const groupKeys = groups.map(group => group.key)
      projectAccordionModel.value = projectAccordionModel.value.filter(key => groupKeys.includes(key))
    }

    const groupKeys = new Set(projectGroups.value.map(group => group.key))
    const nextSelectedFolders: Record<string, string> = {}

    projectGroups.value.forEach(group => {
      nextSelectedFolders[group.key] = getSelectedFolderForGroup(group)
    })

    Object.keys(selectedFolderByGroup.value).forEach(groupKey => {
      if (groupKeys.has(groupKey)) {
        nextSelectedFolders[groupKey] = nextSelectedFolders[groupKey] || selectedFolderByGroup.value[groupKey]
      }
    })

    selectedFolderByGroup.value = nextSelectedFolders
    if (props.modelValue && hasLoadedFiles.value) void loadOpenProjectPages()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  cancelFileRequests()
  unsubscribeWaitingRoomAutoSyncRefresh()
  if (fileSearchDebounce) {
    clearTimeout(fileSearchDebounce)
    fileSearchDebounce = null
  }
})

const onProjectAccordionUpdate = (value: unknown) => {
  const nextValue = Array.isArray(value)
    ? value.map(String)
    : value
      ? [String(value)]
      : []

  if (!canCollapseProjectAccordions.value && visibleProjectGroups.value[0]) {
    projectAccordionModel.value = [visibleProjectGroups.value[0].key]
    void loadOpenProjectPages()
    return
  }

  projectAccordionModel.value = nextValue
  for (const [groupKey, request] of pageRequests) {
    if (!nextValue.includes(groupKey)) {
      request.controller.abort()
      pageRequests.delete(groupKey)
      const loading = new Set(loadingPageGroups.value)
      loading.delete(groupKey)
      loadingPageGroups.value = loading
    }
  }
  void loadOpenProjectPages()
}

const onFolderPageUpdate = async (folder: string, page: number) => {
  setFolderPage(folder, page)
  await loadOpenProjectPages(folder)
}

const onFolderPerPageUpdate = async (folder: string, perPage: number) => {
  setFolderPerPage(folder, perPage)
  await loadOpenProjectPages(folder)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="95vw"
    max-height="95vh"
    :width="dialogWidth"
    :height="dialogHeight"
    class="project-files-resizable-dialog"
    @update:model-value="onDialogModelValueUpdate"
  >
    <DialogResizeHandle
      v-model:dialogWidth="dialogWidth"
      v-model:dialogHeight="dialogHeight"
      :min-width="dialogMinWidth"
      :min-height="dialogMinHeight"
    />

    <VCard class="project-files-dialog-card d-flex flex-column h-100">
      <VCardTitle class="vss-movable" >
        <MovableDialog />
        <div class="d-flex align-center justify-space-between">
          <span>Project Files</span>
          <div class="d-flex align-center gap-2">
            <VBtn
              v-if="can('view', 'fmwaitingroom')"
              color="primary"
              variant="tonal"
              size="small"
              prepend-icon="tabler-report"
              @click="showWaitingRoomDialog = true"
            >
              Waiting Room
            </VBtn>
            <VBtn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeDialog"
            />
          </div>
        </div>
      </VCardTitle>

      <VCardText class="project-files-dialog-content">
        <VRow class="mb-2" align="center">
          <VCol cols="12" :md="showProgramFilter ? 4 : 6">
            <VTextField
              v-model="fileSearch"
              label="Search file"
              prepend-inner-icon="tabler-search"
              clearable
              hide-details
              variant="outlined"
            />
          </VCol>
          <VCol v-if="showProgramFilter" cols="12" md="3">
            <MultipleEventGroupSelect
              v-model="selectedPrograms"
              :select-options="programOptions"
              label="Programs"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="5" class="d-flex justify-end gap-4">
            <VBtn
              color="primary"
              variant="outlined"
              :loading="filesLoading"
              @click="getProjectFiles"
            >
              Refresh
            </VBtn>
          </VCol>
        </VRow>

        <VExpansionPanels
          :model-value="projectAccordionModel"
          multiple
          variant="accordion"
          @update:model-value="onProjectAccordionUpdate"
          gap='8'
        >
          <VExpansionPanel
            v-for="group in visibleProjectGroups"
            :key="group.key"
            :value="group.key"
            :readonly="!canCollapseProjectAccordions"
          >
            <VExpansionPanelTitle
              :class="{
                'project-files-accordion-title': true,
                'project-files-accordion-title--readonly': !canCollapseProjectAccordions,
                'mb-1': true,
                'br-4': true,
              }"
            >
              <div class="d-flex align-center justify-space-between w-100 pr-2">
                <div class="d-flex align-center gap-2 text-truncate">
                  <span class="text-truncate">{{ group.title }}</span>
                  <VMenu
                    v-if="group.selectedItems.length"
                    open-on-hover
                    location="end"
                  >
                    <template #activator="{ props: menuProps }">
                      <VChip
                        v-bind="menuProps"
                        size="small"
                        color="primary"
                        variant="tonal"
                        class="flex-shrink-0"
                        @click.stop
                      >
                        {{ group.selectedItems.length }} items selected
                      </VChip>
                    </template>
                    <div>
                      <VList density="compact">
                        <VListItem>
                          <h4>{{ group.selectedItems.length }} items selected with filenames:</h4>
                        </VListItem>
                        <VListItem
                          v-for="identity in getSelectedIdentities(group)"
                          :key="identity"
                          :title="identity"
                        />
                      </VList>
                    </div>
                  </VMenu>
                </div>
              </div>
              <template
                v-if="!canCollapseProjectAccordions"
                #actions
              >
              </template>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="project-files-folder-controls">
                <VBtnToggle
                  :model-value="getSelectedFolderForGroup(group)"
                  mandatory
                  divided
                  color="primary"
                  variant="outlined"
                  class="project-files-folder-toggle"
                  @update:model-value="folder => setSelectedFolderForGroup(group.key, String(folder))"
                >
                  <VBtn
                    v-for="folder in availableFolders"
                    :key="`${group.key}-${folder.value}`"
                    :value="folder.value"
                    size="large"
                    class='rounded-0'
                  >
                    {{ folder.title }}
                  </VBtn>
                  <VBtn
                    :value="allFoldersValue"
                    size="large"
                    class='rounded-0'
                  >
                    All
                  </VBtn>
                </VBtnToggle>
              </div>

              <DivTable
                v-model="selectedFileKeys"
                :items="getSelectedFolderCard(group)?.files || []"
                :items-length="getSelectedFolderCard(group)?.count || 0"
                :headers="fileTableHeaders"
                :loading="metadataLoading || loadingPageGroups.has(group.key)"
                :page="getSelectedFolderCard(group)?.page || 1"
                :items-per-page="getSelectedFolderCard(group)?.perPage || defaultFolderPerPage"
                :items-per-page-options="folderPerPageOptions"
                :sort-by="fileTableSortBy"
                item-value="selectionKey"
                show-select
                no-data-text="No files"
                :grid-id="getFileTableGridId(group.key)"
                :grid-storage-id="fileTableGridStorageId"
                hover
                table-class="project-files-table"
                scrollbar-class="project-files-table-scrollbar"
                @update:options="options => onFileTableOptionsUpdate(getSelectedFolderForGroup(group), options)"
              >
                <template #top>
                  <div class="d-flex justify-space-between align-center flex-wrap mb-2 gap-2">
                    <div class="d-flex align-center gap-2">
                      <VChip size="small" color="primary" variant="tonal">
                        {{ getSelectedFolderCard(group)?.count || 0 }}
                      </VChip>
                      <span class="text-caption text-medium-emphasis">
                        {{ getSelectedFolderCard(group)?.title || 'Folder' }}
                      </span>
                    </div>
                    <div class="d-flex align-center gap-4">
                      <VBtn
                        color="primary"
                        :variant="getSelectedFilesForGroup(group).length === 0 || bulkDownloadLoading ? 'outlined' : 'flat'"
                        :loading="bulkDownloadScope === projectDownloadScopeKey(group)"
                        :disabled="getSelectedFilesForGroup(group).length === 0 || bulkDownloadLoading"
                        prepend-icon="tabler-download"
                        size="small"
                        @click="handleProjectBulkDownload(group)"
                      >
                        Download{{ getSelectedFilesForGroup(group).length ? ` (${getSelectedFilesForGroup(group).length})` : '' }}
                      </VBtn>
                      <ProjectFilesItemTooltip
                        content="Table column settings"
                        v-slot="{ triggerProps }"
                      >
                        <VBtn
                          v-bind="triggerProps"
                          variant="elevated"
                          density="compact"
                          color="secondary"
                          icon="mdi-wrench"
                          class="rounded-lg"
                          @click="showTableColumnSettingsDialog = true"
                        >
                          <VIcon icon="mdi-wrench" />
                        </VBtn>
                      </ProjectFilesItemTooltip>
                    </div>
                  </div>
                </template>

                <template #item.preview="{ item }">
                  <button
                    v-if="hasFileThumbnail(item.raw)"
                    type="button"
                    class="project-file-thumb project-file-thumb-button"
                    :aria-label="`Preview ${item.raw.filename}`"
                    :aria-busy="loadingPreview.has(item.raw.key)"
                    :disabled="loadingPreview.has(item.raw.key)"
                    @click.stop="handlePreview(item.raw)"
                  >
                    <img
                      :src="item.raw.previewUrl || ''"
                      :alt="item.raw.filename"
                      class="project-file-thumb-img"
                      @error="markPreviewBroken(item.raw.previewUrl)"
                    />
                    <span
                      v-if="loadingPreview.has(item.raw.key)"
                      class="project-file-thumb-loader"
                    >
                      <VProgressCircular
                        indeterminate
                        color="primary"
                        size="20"
                        width="2"
                      />
                    </span>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="project-file-thumb"
                    :aria-label="`Preview ${item.raw.filename}`"
                    :aria-busy="loadingPreview.has(item.raw.key)"
                    :disabled="loadingPreview.has(item.raw.key)"
                    @click.stop="handlePreview(item.raw)"
                  >
                    <VIcon
                      :icon="getFileIcon(item.raw.filename)"
                      :color="getFileIconColor(item.raw.filename)"
                      size="24"
                    />
                    <span
                      v-if="loadingPreview.has(item.raw.key)"
                      class="project-file-thumb-loader"
                    >
                      <VProgressCircular
                        indeterminate
                        color="primary"
                        size="20"
                        width="2"
                      />
                    </span>
                  </button>
                </template>

                <template #item.filename="{ item }">
                  <div class="d-flex align-center gap-1">
                    <ProjectFilesItemTooltip
                      v-if="canReassign(item.raw)"
                      content="Rename"
                      v-slot="{ triggerProps }"
                    >
                      <VBtn
                        v-bind="triggerProps"
                        icon="mdi-pencil"
                        variant="text"
                        size="x-small"
                        color="primary"
                        aria-label="Rename file"
                        @click.stop="openRenameDialog(item.raw)"
                      />
                    </ProjectFilesItemTooltip>
                    <span>{{ getFileTitle(item.raw) }}</span>
                  </div>
                </template>

                <template #item.statuses="{ item }">
                  <span class="text-truncate d-block">
                    {{ item.raw.statuses.join(', ') || '-' }}
                  </span>
                </template>

                <template #item.created="{ item }">
                  {{ formatDate(item.raw.created) }}
                </template>

                <template #item.linked_items="{ item }">
                  {{ getLinkedItemsCount(item.raw) }}
                </template>

                <template #item.versions="{ item }">
                   <VChip
                        v-if="item.raw.list_state === 'historical'"
                        size="x-small"
                        color="warning"
                        variant="tonal"
                        class="ml-2"
                      >
                        Historical
                      </VChip>
                  <span class="ml-2">
                    {{ item.raw.versionCount || '-' }}
                  </span>
                </template>

                <template #item.actions="{ item }">
                  <div class="d-flex align-center gap-1">
                    <ProjectFilesItemTooltip
                      v-if="can('view', 'fmfilecontrol')"
                      content="Control"
                      v-slot="{ triggerProps }"
                    >
                      <VBtn
                        v-bind="triggerProps"
                        icon
                        variant="text"
                        size="small"
                        :color="getActionColor(item.raw)"
                        @click.stop="openControlDialog(item.raw)"
                      >
                        <VIcon icon="tabler-adjustments" size="18" />
                      </VBtn>
                    </ProjectFilesItemTooltip>

                    <!-- removed the preview button, because it's duplicated with the thumbnail click, and it was causing confusion for users. The thumbnail itself is now the trigger for previewing the file. -->
                    <!-- <ProjectFilesItemTooltip -->
                    <!--   content="Preview" -->
                    <!--   v-slot="{ triggerProps }" -->
                    <!-- > -->
                    <!--   <VBtn -->
                    <!--     v-bind="triggerProps" -->
                    <!--     icon -->
                    <!--     variant="text" -->
                    <!--     size="small" -->
                    <!--     :color="getActionColor(item.raw)" -->
                    <!--     :loading="loadingPreview.has(item.raw.key)" -->
                    <!--     :style="{ visibility: isPreviewableFile(item.raw.filename) ? 'visible' : 'hidden' }" -->
                    <!--     @click.stop="handlePreview(item.raw)" -->
                    <!--   > -->
                    <!--     <VIcon icon="tabler-eye" size="18" /> -->
                    <!--   </VBtn> -->
                    <!-- </ProjectFilesItemTooltip> -->

                    <ProjectFilesItemTooltip
                      content="Download"
                      v-slot="{ triggerProps }"
                    >
                      <VBtn
                        v-bind="triggerProps"
                        icon
                        variant="text"
                        size="small"
                        :color="getActionColor(item.raw)"
                        :loading="loadingDownload.has(item.raw.key)"
                        @click.stop="handleDownload(item.raw)"
                      >
                        <VIcon icon="tabler-download" size="18" />
                      </VBtn>
                    </ProjectFilesItemTooltip>

                  </div>
                </template>

                <template #item.folder="{ item }">
                  {{ item.raw.folder }}
                </template>

              </DivTable>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn variant="text" @click="closeDialog">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <TableColumnSettingsDialog
    v-model="showTableColumnSettingsDialog"
    :headers="fileTableHeaders"
    :default-headers="defaultFileTableHeaders"
    @onUpdate="onUpdateTableColumns"
  />

  <ProjectFileControlDialog
    v-if="can('view', 'fmfilecontrol')"
    v-model="showControlDialog"
    :project-id="selectedControlFile?.projectId || projectId"
    :file-key="selectedControlFile?.key || null"
  />

  <VDialog v-model="showRenameDialog" max-width="560">
    <VCard>
      <VCardTitle class="vss-movable">Rename File</VCardTitle>
      <VCardText>
        <p class="text-body-2 mb-4">Select the item name to assign to this file.</p>
        <VAutocomplete
          v-model="selectedRenameOption"
          :items="renameOptions"
          item-title="identity"
          return-object
          label="New name"
          :loading="renameOptionsLoading"
          no-filter
          clearable
          @update:search="loadRenameOptions"
        >
        </VAutocomplete>
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn variant="text" @click="showRenameDialog = false">Cancel</VBtn>
        <VBtn color="primary" :disabled="!selectedRenameOption" @click="confirmRename">Continue</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <VDialog v-model="showRenameConfirmDialog" max-width="480">
    <VCard>
      <VCardTitle>Confirm rename</VCardTitle>
      <VCardText>Are you sure you want to rename the file? This may create a duplicate.</VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn variant="text" :disabled="renameLoading" @click="showRenameConfirmDialog = false">Cancel</VBtn>
        <VBtn color="warning" :loading="renameLoading" @click="submitRename">Rename</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <ProjectFilesWaitingRoomDialog
    v-model="showWaitingRoomDialog"
    :default-project-type-id="projectTypeId"
    :initial-projects="waitingRoomProjectOptions"
  />

  <ProjectFilePreviewDialog
    v-model="previewDialogVisible"
    :url="previewUrl"
    :filename="previewFilename"
    :loading="isPreviewLoading"
  />
</template>

<style scoped lang="scss">
:deep(.project-files-resizable-dialog .v-overlay__content) {
  max-width: 95vw;
  max-height: 95vh;
}

.project-files-dialog-card {
  position: relative;
  overflow: hidden;
}

.project-files-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 0 !important;
}

:deep(.v-expansion-panel--active > .v-expansion-panel-title) {
  color: rgb(221, 8, 43);
  border-radius: 12px;
}

.project-files-accordion-title--readonly {
  cursor: default !important;
}

:deep(.v-expansion-panel--active > .v-expansion-panel-title .v-icon),
:deep(.v-expansion-panel--active > .v-expansion-panel-title .v-chip) {
  color: rgb(221, 8, 43);
}

.project-files-folder-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.project-files-folder-toggle {
  block-size: auto !important;
  inline-size: fit-content;
  max-inline-size: 100%;
  flex-wrap: wrap;
  border-radius: 8px;

  :deep(.v-btn) {
    min-inline-size: 0;
    white-space: normal;
    min-height: 48px;
  }
  :deep(.v-btn):first-child {
    border-top-left-radius: 8px !important;
    border-bottom-left-radius: 8px  !important;
  }
  :deep(.v-btn):last-child {
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px  !important;
  }
}

:deep(.project-files-table .grid-cell) {
  min-inline-size: 0;
}

:deep(.project-files-table .div-grid-header .grid-cell) {
  font-size: 11px;
  line-height: 1.2;
  padding-block: 8px;
}

:deep(.project-files-table .grid-cell) {
  font-size: 10px;
  line-height: 1.2;
  padding-block: 8px;
}

:deep(.project-files-table .div-grid-row:hover) {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.project-file-thumb {
  position: relative;
  inline-size: 40px;
  block-size: 40px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}

.project-file-thumb:disabled {
  cursor: wait;
}

.project-file-thumb-button {
  padding: 0;
  cursor: pointer;
}

.project-file-thumb-button:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.project-file-thumb-img {
  inline-size: 100%;
  block-size: 100%;
  object-fit: contain;
  display: block;
}

.project-file-thumb-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-surface), 0.72);
}
</style>
